// *** HTTP REQUEST & RESPONSE HANDLING (AXIOS) ***

import 'izitoast/dist/css/iziToast.min.css';
import axios from 'axios';
import store from '@/store';

// CREATE AXIOS INSTANCE
const http = axios.create({
    baseURL: process.env.VUE_APP_API_URL
});

// *** DELETE AUTH TOKEN FROM REQUEST HEADER ***
export const deleteHeaderToken = () => {
    delete http.defaults.headers.common['AuthToken'];
};

// *** GET AUTH TOKEN FROM REQUEST HEADER ***
export const getHeaderToken = () => {
    return http.defaults.headers.common['AuthToken'];
};

// *** SET AUTH TOKEN IN REQUEST HEADER ***
export const setHeaderToken = token => {
    http.defaults.headers.common['AuthToken'] = token;
};

// *** GET AUTH TOKEN FROM LOCAL STORAGE ***
const getToken = () => {
    return localStorage.getItem('token');
};

// *** AUTH INTERCEPTOR MIDDLEWARE ***
// Called before sending request to backend.
const authInterceptor = async config => {
    try {
        if (
            !config.url.match(/^(login-logout-log|web-user-failed-login|web-user-password-reset)$/) &&
            !getHeaderToken()
        ) {
            await store.dispatch('account/setToken');
        }
        config.headers['AuthToken'] = getToken();
    } catch (error) {
        console.error('authInterceptor: getIdToken error:', error);
    }
    return config;
};

// *** ERROR INTERCEPTOR MIDDLEWARE ***
// Called after receiving error (4xx) response from backend.
const errorInterceptor = async error => {
    if (!error.response) {
        // no response field indicates server error
        return Promise.reject({ message: 'error_network_server_error' });
    }

    // handle error codes
    let msg = error.response.data || '';
    switch (error.response.status) {
        case 400:
            console.error(error.response.status, msg);
            break;

        case 401: // unauthorized
            if (msg === 'auth/id-token-expired' && !error.config.retry && getToken()) {
                // if id token expired and not retried yet, refresh id token and retry request
                try {
                    await store.dispatch('account/setToken'); // try to refresh id token
                    error.config.retry = true;
                    return http.request(error.config);
                } catch (error) {
                    // if token refresh fails, set "session expired" error message
                    msg = 'error_session_expired';
                }
            }
            // if not returned yet, session has expired -> log out user with error message
            store.dispatch('account/logout', { msg });
            console.error(error.response.status, msg);
            break;

        default:
            console.error(error.response.status, msg);
    }

    return Promise.reject(error);
};

// *** RESPONSE INTERCEPTOR MIDDLEWARE ***
// Called after receiving success (200) response from backend.
const responseInterceptor = response => {
    switch (response.status) {
        case 200:
            break;
        default:
            break;
    }
    return response;
};

// USE INTERCEPTOR MIDDLEWARE
http.interceptors.request.use(authInterceptor);
http.interceptors.response.use(responseInterceptor, errorInterceptor);

export { http };
